import { useMemo, useState } from 'react';
import { Card, CardContent, EmptyState, PostLink } from '../../ui';
import { EMOTION_META, CHART_ORDER, STANCES, STANCE_META } from '../../../constants/sentiment';
import { cn } from '../../../utils/cn';

/**
 * How each post landed.
 *
 * This replaces the emotion grid, which asked one matrix to carry eleven
 * categorical emotions across ten posts, abbreviated to Adm/Joy/Amu/Sur/Con/
 * Sad/Fear/Dpt/Dis/Ang/Neu in eleven unordered hues. Eleven hues cannot be
 * ranked by eye, so every cell had to be read; most cells were near empty; and
 * the sort control offered fifteen options. On a phone it became roughly a
 * hundred and ten coloured chips.
 *
 * The deeper problem was not the layout. The page answered its own question
 * twice and the second answer destroyed the first: "Where the room stands"
 * reduces a pulse to three honest buckets, and then the grid re-asked the same
 * question eleven ways and handed the arithmetic back to the manager.
 *
 * So stance leads and emotion is a qualifier inside it, never a parallel axis.
 * A row is one post: how the room split, what it mostly felt, and a way in.
 * The eleven-way breakdown still exists and still matters, but it belongs in
 * the drawer, where one post has room for it and the parent post is present to
 * give it meaning.
 *
 * Unclear replies sit OUTSIDE the bar, not as a fourth segment. They are an
 * absence of information, not a position between for and against, and the same
 * rule already governs the percentages in "Where the room stands".
 */

const forOf = (r) => (r.stance ? r.stance.for : r.polarity?.positive || 0);
const againstOf = (r) => (r.stance ? r.stance.against : r.polarity?.negative || 0);
const noneOf = (r) => (r.stance ? r.stance.none : r.polarity?.neutral || 0);
const unclearOf = (r) => r.unclear || 0;

/**
 * Three sorts, not fifteen. The old control offered one per emotion, which is
 * eleven ways to ask a question a manager does not have. These are the three
 * that change what you do next.
 */
const SORTS = {
	replies: { label: 'Most replies', fn: (r) => r.replyCount },
	against: { label: 'Most opposition', fn: (r) => (r.replyCount ? againstOf(r) / r.replyCount : 0) },
	newest: { label: 'Newest', fn: (r) => new Date(r.target.postedAt || 0).getTime() },
};

/** The emotion the most replies carried, or null when nothing was labelled. */
function dominantEmotion(r) {
	const emotions = r.emotions || {};
	let best = null;
	let bestCount = 0;
	// CHART_ORDER rather than Object.keys so a tie resolves the same way every
	// render instead of following key insertion order.
	for (const e of CHART_ORDER) {
		if ((emotions[e] || 0) > bestCount) {
			best = e;
			bestCount = emotions[e];
		}
	}
	return best ? { key: best, count: bestCount } : null;
}

function StanceBar({ row, onOpen }) {
	const counts = { for: forOf(row), against: againstOf(row), none: noneOf(row) };
	const positioned = counts.for + counts.against + counts.none;

	if (!positioned) {
		return <span className="text-xs text-muted-foreground">No replies took a position</span>;
	}

	return (
		<span className="flex h-5 w-full overflow-hidden rounded-sm" role="group" aria-label="How the room split">
			{STANCES.map((s) => {
				const n = counts[s];
				if (!n) return null;
				const pct = Math.round((n / positioned) * 100);
				return (
					<button
						key={s}
						type="button"
						style={{ width: `${(n / positioned) * 100}%` }}
						onClick={() => onOpen?.({
							parentPostId: row.target.postId,
							url: row.target.url,
							stance: s,
							title: STANCE_META[s].label,
							text: row.target.text,
						})}
						title={`${n} ${STANCE_META[s].label.toLowerCase()} (${pct}%)`}
						aria-label={`${n} ${STANCE_META[s].label.toLowerCase()}, ${pct} percent`}
						className={cn(
							'h-full min-w-[3px] transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
							STANCE_META[s].bg
						)}
					/>
				);
			})}
		</span>
	);
}

function Mood({ row }) {
	const dominant = dominantEmotion(row);
	if (!dominant) return <span className="text-xs text-muted-foreground">Not labelled</span>;
	const meta = EMOTION_META[dominant.key];
	return (
		<span className="inline-flex items-center gap-1.5 whitespace-nowrap">
			{/* The dot is an aid, never the carrier: the word is always present. */}
			<span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', meta.bg)} />
			<span>{meta.label}</span>
		</span>
	);
}

function Unclear({ row }) {
	const n = unclearOf(row);
	if (!n) return null;
	return (
		<span className="whitespace-nowrap text-xs text-muted-foreground" title="Replies we could not read without more context. Excluded from the split.">
			{n} unclear
		</span>
	);
}

export function PostReactions({ rows = [], onOpen, loading }) {
	const [sort, setSort] = useState('replies');

	const sorted = useMemo(() => {
		const fn = (SORTS[sort] || SORTS.replies).fn;
		return [...rows].sort((a, b) => fn(b) - fn(a));
	}, [rows, sort]);

	if (!loading && rows.length === 0) {
		return (
			<Card>
				<CardContent>
					<EmptyState compact title="No posts read yet" description="This fills in after the first pulse." />
				</CardContent>
			</Card>
		);
	}

	const openAll = (r) => onOpen?.({
		parentPostId: r.target.postId,
		url: r.target.url,
		title: 'All replies',
		text: r.target.text,
	});

	return (
		<Card>
			<CardContent className="p-0">
				<div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
					<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">How each post landed</h3>
					<label className="flex items-center gap-2 text-xs text-muted-foreground">
						Sort by
						<select
							value={sort}
							onChange={(e) => setSort(e.target.value)}
							className="rounded-md border border-input bg-card px-2 py-1 text-sm text-foreground"
						>
							{Object.entries(SORTS).map(([key, s]) => (
								<option key={key} value={key}>{s.label}</option>
							))}
						</select>
					</label>
				</div>

				{/* Desktop: five columns, none of which is an emotion. */}
				<div className="hidden sm:block">
					<table className="w-full table-fixed text-sm">
						<thead>
							<tr className="border-t border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
								<th className="w-[38%] px-4 py-2 font-medium">Post</th>
								<th className="w-16 px-2 py-2 text-right font-medium">Replies</th>
								<th className="px-3 py-2 font-medium">How the room split</th>
								<th className="w-40 px-3 py-2 font-medium">Mostly felt</th>
								<th className="w-24 px-3 py-2 text-right font-medium"><span className="sr-only">Read replies</span></th>
							</tr>
						</thead>
						<tbody>
							{sorted.map((r) => (
								<tr key={r.target.postId} className="border-t border-border align-top">
									<td className="px-4 py-2.5">
										<button
											type="button"
											className="line-clamp-2 text-left text-foreground hover:text-primary"
											onClick={() => openAll(r)}
										>
											{r.target.text || r.target.postId}
										</button>
										<PostLink url={r.target.url} className="mt-1 flex text-xs text-muted-foreground">Open post</PostLink>
									</td>
									<td className="px-2 py-2.5 text-right tabular-nums text-foreground">{r.replyCount}</td>
									<td className="px-3 py-2.5">
										<StanceBar row={r} onOpen={onOpen} />
										<span className="mt-1 block"><Unclear row={r} /></span>
									</td>
									<td className="px-3 py-2.5 text-foreground"><Mood row={r} /></td>
									<td className="px-3 py-2.5 text-right">
										<button
											type="button"
											onClick={() => openAll(r)}
											className="rounded-sm text-xs font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											Read
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* Phone: the same five facts stacked. Not a hundred and ten chips. */}
				<ul className="divide-y divide-border border-t border-border sm:hidden" aria-label="Posts and how they landed">
					{sorted.map((r) => (
						<li key={r.target.postId} className="px-4 py-3">
							<button
								type="button"
								className="line-clamp-2 text-left text-sm text-foreground hover:text-primary"
								onClick={() => openAll(r)}
							>
								{r.target.text || r.target.postId}
							</button>
							<p className="mt-1 text-xs text-muted-foreground">
								{r.replyCount} {r.replyCount === 1 ? 'reply' : 'replies'}
							</p>
							<div className="mt-2"><StanceBar row={r} onOpen={onOpen} /></div>
							<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground">
								<Mood row={r} />
								<Unclear row={r} />
							</div>
							<div className="mt-2 flex items-center justify-between">
								<PostLink url={r.target.url} className="flex text-xs text-muted-foreground">Open post</PostLink>
								<button type="button" onClick={() => openAll(r)} className="text-xs font-medium text-primary hover:underline">Read</button>
							</div>
						</li>
					))}
				</ul>
			</CardContent>
		</Card>
	);
}
